import { computed } from 'vue';
import { useEmployeeStore } from '@/stores/employee.js';

/**
 * Composable для работы с правами доступа (permissions) текущего пользователя
 */
export function usePermissions() {
    const employeeStore = useEmployeeStore();

    // Реактивный массив прав текущего пользователя (через getter который преобразует объект в массив)
    const userPermissions = computed(() => employeeStore.userPermissions);

    /**
     * Проверка наличия конкретного права у пользователя
     * @param {string} permission - код права (например, 'customer.view_customer')
     * @returns {boolean}
     */
    const hasPermission = (permission) => {
        if (import.meta.env.DEV) console.log(`[usePermissions.hasPermission] Checking permission: "${permission}"`);
        if (import.meta.env.DEV) console.log('[usePermissions.hasPermission] currentUser:', employeeStore.currentUser);

        if (!permission) {
            if (import.meta.env.DEV) console.log('[usePermissions.hasPermission] No permission required, granting access');
            return true;
        }

        // Суперпользователь имеет все права
        if (employeeStore.currentUser?.is_superuser === true) {
            if (import.meta.env.DEV) console.log('[usePermissions.hasPermission] User is superuser, granting access');
            return true;
        }

        // Backend возвращает права в поле effective_permissions, а не permissions
        const perms = employeeStore.currentUser?.effective_permissions || employeeStore.currentUser?.permissions;
        if (import.meta.env.DEV) console.log('[usePermissions.hasPermission] Raw permissions:', perms);

        // Если permissions это объект { "perm": true }
        if (perms && typeof perms === 'object' && !Array.isArray(perms)) {
            const result = perms[permission] === true;
            if (import.meta.env.DEV) console.log(`[usePermissions.hasPermission] Object check result:`, result);
            return result;
        }

        // Если permissions это массив
        const result = Array.isArray(perms) && perms.includes(permission);
        if (import.meta.env.DEV) console.log(`[usePermissions.hasPermission] Array check result:`, result);
        return result;
    };

    /**
     * Проверка наличия хотя бы одного из указанных прав
     * @param {string[]} permissions - массив кодов прав
     * @returns {boolean}
     */
    const hasAnyPermission = (permissions) => {
        if (!permissions || permissions.length === 0) return true;
        return permissions.some(perm => hasPermission(perm));
    };

    /**
     * Проверка наличия всех указанных прав
     * @param {string[]} permissions - массив кодов прав
     * @returns {boolean}
     */
    const hasAllPermissions = (permissions) => {
        if (!permissions || permissions.length === 0) return true;
        return permissions.every(perm => hasPermission(perm));
    };

    return {
        userPermissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
    };
}
